import React, {useState, useEffect} from "react";
import { useDispatch }              from "react-redux";
import classnames                   from 'classnames';
import numeral                      from 'numeral';

import { FontAwesomeIcon }          from '@fortawesome/react-fontawesome';
import { faExclamationTriangle }    from '@fortawesome/free-solid-svg-icons';
import { faSearch }                 from '@fortawesome/free-solid-svg-icons';
import { faSync }                   from '@fortawesome/free-solid-svg-icons';

import OverlayTrigger               from 'react-bootstrap/OverlayTrigger';
import Tooltip                      from 'react-bootstrap/Tooltip';

import ExtraParts                   from "./qty/extra_parts";
import UnitPrice                    from "./unit_price/unit_price";
import Stock                        from "./qty/stock";
import TooltipComponent             from "pu-react/components/Tooltip";

import { 
  valid_buy_options, 
  hasValidPartSupplierSelections,
  getEstimatedTariff,
} from "product_tabs/tab_parts/utils/helpers";

import {
  extractBomLineItemData,
  getPartSupplierSelections,
} from "product_tabs/tab_parts/utils/extracters";

import { 
  changeBomUserAction, 
} from "product_tabs/tab_parts/bomSlice";

const getSuppliers = (part, selected_part_supplier, board_qty) => {
  const ps_selections = getPartSupplierSelections(part, board_qty)
  if(!selected_part_supplier && ps_selections.length == 0) return null
  if(ps_selections.length > 0) return ps_selections.map(ps => ps.supplier.name).join(", ")
  return selected_part_supplier.supplier.name
}

const BomLineItem = (props) => {
  const dispatch = useDispatch();

  const {
    row,
    bom_line_item,
    selected_board_qty,
    markups,
    onShowSearchModal,
    onShowMoreOptions,
  } = props;

  if(!bom_line_item) return null

  const {
    original_mpn,
    quote_status,
    user_action,
    line_in_file,
  } = bom_line_item;

  const {
    new_status: board_qty_status,
    identifier,
    description,
    stock,
    tariffs,
    part_selected,
    part_supplier_selected,
    tariff_may_be_applied_after_purchase,
  } = extractBomLineItemData(bom_line_item, selected_board_qty)

  const index = (row - 1)
  const exact_qty = bom_line_item.qty * selected_board_qty
  const extra_parts = bom_line_item.extra_parts[selected_board_qty] || 0
  const order_qty = exact_qty + extra_parts
  const minimum_order_qty = bom_line_item.calculated_minimun_order_qty ? (bom_line_item.calculated_minimun_order_qty[selected_board_qty] || 0) : 0
  const qty_to_buy = bom_line_item.qty_to_buy && bom_line_item.qty_to_buy[selected_board_qty] ? bom_line_item.qty_to_buy[selected_board_qty] : 0
  const total_price = bom_line_item.price_by_qty && bom_line_item.price_by_qty[selected_board_qty] ? bom_line_item.price_by_qty[selected_board_qty] : 0
  const error_message = bom_line_item.error_message[selected_board_qty]
  const unit_price = total_price > 0 ? ((bom_line_item.price_by_qty[selected_board_qty] / bom_line_item.qty_to_buy[selected_board_qty]) * (1 + markups[selected_board_qty])) : 0
  const allow_edit = !hasValidPartSupplierSelections(part_selected, selected_board_qty)
  const has_error = error_message && error_message != ""
  const show_qty_price = !has_error && quote_status != 'pending' && valid_buy_options.includes(user_action) 
  const suppliers = getSuppliers(part_selected, part_supplier_selected, selected_board_qty)
  const estimatedTariff = getEstimatedTariff(tariffs)

  const onChangeUserAction = (user_action) => {
    dispatch(changeBomUserAction({index, user_action}))
  }

  const onSearchSubstitutes = () => {
    onShowSearchModal(bom_line_item)
  }

  const onShowOffers = (e) => {
    e.preventDefault();
    onShowMoreOptions(bom_line_item)
  }

  return (
    <tr className={classnames("", {error: has_error})}>
      <td>
        {has_error && 
          <TooltipComponent id={`bli-row-tooltip-${row}`} title={error_message} icon={faExclamationTriangle}/>
        }
        {quote_status == 'pending' && 
          <OverlayTrigger overlay={<Tooltip id={`bli-row-tooltip-${row}`}>Part is being quoted</Tooltip>}>
            <FontAwesomeIcon icon={faSync} spin/>
          </OverlayTrigger>
        }
      </td>
      <td>{line_in_file}</td>
      <td>
        {isManager && quote_status != 'pending' ? (
          <a href="#" onClick={onShowOffers}>{identifier}</a>
        ) : (
          <>{identifier}</>
        )}  
        {quote_status != 'pending' && identifier != original_mpn && original_mpn != "" && 
          <>
            <br/>
            <span className="text-muted"><small className="text-decoration-line-through">{original_mpn}</small></span>
          </>
        }
        {isManager && quote_status != 'pending' && suppliers && 
          <>
            <br/>
            <small><i>{suppliers}</i></small>
          </>
        }
      </td>
      <td>{description}</td>
      <td className="extra-parts">
        <ExtraParts 
          index={index}
          board_qty={selected_board_qty}
          part_selected={part_selected}
          part_supplier_selected={part_supplier_selected}
          show_qty_price={show_qty_price}
          calculated_minimun_order_qty={minimum_order_qty}
          exact_qty={exact_qty}
          extra_parts={extra_parts}
          user_action={user_action}
          allow_edit={allow_edit}/>
      </td>
      <td className="stock">
        <Stock 
          status={board_qty_status}
          quote_status={quote_status}
          row={row}
          exact_qty={exact_qty}
          extra_parts={extra_parts}
          stock={stock}
          qty_to_buy={qty_to_buy}
          has_error={has_error}
          show_qty_price={show_qty_price}/>
      </td>
      <td className="price">
        <UnitPrice 
          index={index}
          board_qty={selected_board_qty}
          exact_qty={exact_qty}
          minimum_order_qty={minimum_order_qty}
          markup={markups[selected_board_qty]}
          part={part_selected}
          part_supplier_selected={part_supplier_selected}
          unit_price={unit_price}
          user_action={user_action}
          allow_edit={allow_edit}/>
      </td>
      <td className="tariff">
        {tariff_may_be_applied_after_purchase && 
          <OverlayTrigger overlay={<Tooltip id={`bli-row-tooltip-tariff-${row}`}>Tariff may apply after purchase</Tooltip>}>
            <span><i className="fas fa-dollar-sign"></i></span>
          </OverlayTrigger>
        }
        {!tariff_may_be_applied_after_purchase ? estimatedTariff : ''}
      </td>
      <td>
        {quote_status == 'processed' && 
          <select className="form-select form-select-sm"
          onChange={e => onChangeUserAction(e.target.value)}
          value={user_action || ''}>
            <option value="">Select Action</option>
            {stock > 0 && stock >= order_qty && 
              <option value="buy">Buy</option>
            }
            {stock > 0 && stock < order_qty && 
              <option value="buy_stock">Buy all stock</option>
            }
            <option value="will_provide">I will provide</option>
            <option value="do_not_populate">Do not populate</option>
          </select>
        }
        
      </td>
      <td className="text-center">
        {quote_status == 'processed' && 
          <button type="button" className="btn btn-link" onClick={onSearchSubstitutes}>
            <i><FontAwesomeIcon icon={faSearch}/></i>
          </button>
        }
      </td>
    </tr>
  )
}

export default BomLineItem;